import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, RotateCcw, Share2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { AdBanner } from "@/components/AdBanner";
import { PlayGate } from "@/components/PlayGate";
import { quizById } from "@/data/games";
import { usePlayGate } from "@/hooks/usePlayGate";
import { Button } from "@/components/ui/button";

export default function QuizPage() {
  const { quizId } = useParams();
  const quiz = quizById(quizId);
  const { blocked, playsLeft, registerPlay, isAuthed } = usePlayGate();
  const [gateBlocked, setGateBlocked] = useState(false);
  const [step, setStep] = useState(0);
  const [counts, setCounts] = useState({});
  const [result, setResult] = useState(null);
  const registered = useRef(false);

  useEffect(() => {
    if (!quiz || registered.current) return;
    registered.current = true;
    if (!registerPlay()) setGateBlocked(true);
  }, [quiz, registerPlay]);

  useEffect(() => { setGateBlocked(blocked); }, [blocked]);

  if (!quiz) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="font-display font-black text-3xl uppercase">Quiz not found</p>
        <Link to="/quizzes" className="text-neon-cyan hover:underline mt-4 inline-block">← Back to quizzes</Link>
      </div>
    );
  }

  const answer = (type) => {
    const nc = { ...counts, [type]: (counts[type] || 0) + 1 };
    setCounts(nc);
    if (step + 1 >= quiz.questions.length) {
      const top = Object.entries(nc).sort((a, b) => b[1] - a[1])[0][0];
      setResult(quiz.results[top]);
    } else {
      setStep(step + 1);
    }
  };

  const restart = () => { setStep(0); setCounts({}); setResult(null); };

  const share = () => {
    const text = `I'm "${result.title}" on Video Game Mania! What are you? ${window.location.href}`;
    if (navigator.share) navigator.share({ title: quiz.title, text }).catch(() => {});
    else { navigator.clipboard.writeText(text); toast.success("Result copied — share it with friends!"); }
  };

  const progress = result ? 100 : ((step) / quiz.questions.length) * 100;

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-12" data-testid="quiz-page">
      <Seo title={quiz.title} description={`${quiz.blurb} Take this fun quiz free on Video Game Mania.`} />
      <Link to="/quizzes" className="inline-flex items-center gap-1 text-zinc-400 hover:text-neon-cyan text-sm font-semibold uppercase tracking-widest mb-6">
        <ArrowLeft className="w-4 h-4" /> Quizzes
      </Link>

      <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter mb-2">{quiz.title}</h1>
      {!isAuthed && playsLeft !== Infinity && !gateBlocked && (
        <span className="font-mono text-xs text-neon-yellow">{playsLeft} free plays left</span>
      )}

      <div className="mt-6 bg-zinc-950 border-2 border-zinc-800 rounded-2xl overflow-hidden">
        {gateBlocked ? (
          <PlayGate playsLeft={playsLeft} />
        ) : result ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-8 text-center" data-testid="quiz-result">
            <p className="text-zinc-400 uppercase tracking-widest text-xs mb-2">You are…</p>
            <h2 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter text-neon-cyan text-glow mb-4">{result.title}</h2>
            <p className="text-zinc-300 max-w-md mx-auto mb-8">{result.desc}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={share} data-testid="quiz-share" className="bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest">
                <Share2 className="w-4 h-4 mr-2" /> Share Result
              </Button>
              <Button onClick={restart} variant="outline" className="border-zinc-700 text-white rounded-none font-bold uppercase tracking-widest">
                <RotateCcw className="w-4 h-4 mr-2" /> Retake
              </Button>
            </div>
          </motion.div>
        ) : (
          <div className="p-8">
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-6">
              <div className="h-full bg-neon-cyan transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="font-mono text-xs text-zinc-500 mb-2">Question {step + 1} of {quiz.questions.length}</p>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h3 className="font-display font-bold text-2xl mb-6">{quiz.questions[step].q}</h3>
                <div className="space-y-3">
                  {quiz.questions[step].options.map((opt, i) => (
                    <button key={i} onClick={() => answer(opt.type)} data-testid={`quiz-option-${i}`}
                      className="w-full text-left px-5 py-4 rounded-lg bg-zinc-900 border-2 border-zinc-700 hover:border-neon-cyan hover:bg-zinc-800 font-semibold transition-colors">
                      {opt.text}
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>

      <AdBanner slot="quiz" className="mt-8" />
    </div>
  );
}
